import React, { useState, useEffect, useMemo } from 'react'
import api from '../api/axios'
import { Search, Copy, Check, X, Grid, List, ArrowUpDown } from 'lucide-react'

type ColorItem = { code: string, hex: string, family: string }

function hexToRgb(hex: string): { r: number, g: number, b: number } {
  const clean = hex.replace('#', '')
  const r = parseInt(clean.substring(0, 2), 16)
  const g = parseInt(clean.substring(2, 4), 16)
  const b = parseInt(clean.substring(4, 6), 16)
  return { r, g, b }
}

function rgbToCmyk(r: number, g: number, b: number): { c: number, m: number, y: number, k: number } {
  const rf = r / 255, gf = g / 255, bf = b / 255
  const k = 1 - Math.max(rf, gf, bf)
  if (k === 1) return { c: 0, m: 0, y: 0, k: 100 }
  const c = (1 - rf - k) / (1 - k)
  const m = (1 - gf - k) / (1 - k)
  const y = (1 - bf - k) / (1 - k)
  return { c: Math.round(c * 100), m: Math.round(m * 100), y: Math.round(y * 100), k: Math.round(k * 100) }
}

function hexToHue(hex: string): number {
  const { r, g, b } = hexToRgb(hex)
  const rf = r / 255, gf = g / 255, bf = b / 255
  const max = Math.max(rf, gf, bf), min = Math.min(rf, gf, bf)
  const delta = max - min
  let h = 0
  if (delta === 0) h = 0
  else if (max === rf) h = ((gf - bf) / delta) % 6
  else if (max === gf) h = (bf - rf) / delta + 2
  else h = (rf - gf) / delta + 4
  h = Math.round(h * 60)
  if (h < 0) h += 360
  return h
}

function applyTint(hex: string, tintPct: number): string {
  const { r, g, b } = hexToRgb(hex)
  const factor = 1 - tintPct / 100
  const tintedR = Math.round(r + (255 - r) * factor)
  const tintedG = Math.round(g + (255 - g) * factor)
  const tintedB = Math.round(b + (255 - b) * factor)
  const toHex = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0')
  return `#${toHex(tintedR)}${toHex(tintedG)}${toHex(tintedB)}`.toUpperCase()
}

function rgbDistance(r1: number, g1: number, b1: number, r2: number, g2: number, b2: number): number {
  return Math.sqrt(Math.pow(r1 - r2, 2) + Math.pow(g1 - g2, 2) + Math.pow(b1 - b2, 2))
}

function getColorName(hex: string): string {
  const clean = hex.replace('#', '')
  if (clean.length !== 6) return ''
  const r = parseInt(clean.substring(0, 2), 16)
  const g = parseInt(clean.substring(2, 4), 16)
  const b = parseInt(clean.substring(4, 6), 16)
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2 / 255
  const s = max === min ? 0 : (max - min) / 255 / (l < 0.5 ? (max + min) / 255 : 2 - (max + min) / 255)

  // Neutral detection first
  if (s < 0.12) {
    if (l > 0.85) return 'white'
    if (l < 0.15) return 'black'
    return 'gray grey'
  }

  const hue = hexToHue(hex)

  // Lightness-based variants
  const lightPrefix = l > 0.7 ? 'light pale ' : ''
  const darkPrefix = l < 0.25 ? 'dark deep ' : ''

  if (hue < 15 || hue >= 345) return `${darkPrefix}${lightPrefix}red`
  if (hue < 30) return `${darkPrefix}${lightPrefix}red orange`
  if (hue < 50) return `${darkPrefix}${lightPrefix}orange`
  if (hue < 65) return `${darkPrefix}${lightPrefix}yellow orange amber`
  if (hue < 80) return `${darkPrefix}${lightPrefix}yellow`
  if (hue < 150) return `${darkPrefix}${lightPrefix}green`
  if (hue < 165) return `${darkPrefix}${lightPrefix}teal green`
  if (hue < 195) return `${darkPrefix}${lightPrefix}teal cyan`
  if (hue < 220) return `${darkPrefix}${lightPrefix}blue cyan`
  if (hue < 255) return `${darkPrefix}${lightPrefix}blue`
  if (hue < 270) return `${darkPrefix}${lightPrefix}blue purple violet`
  if (hue < 290) return `${darkPrefix}${lightPrefix}purple violet`
  if (hue < 320) return `${darkPrefix}${lightPrefix}purple magenta`
  if (hue < 345) return `${darkPrefix}${lightPrefix}pink magenta red`
  return `${darkPrefix}${lightPrefix}red`
}

function naturalCodeSort(a: string, b: string): number {
  const numA = parseFloat(a.replace(/[^0-9.]/g, ''))
  const numB = parseFloat(b.replace(/[^0-9.]/g, ''))
  if (!isNaN(numA) && !isNaN(numB) && numA !== numB) return numA - numB
  return a.localeCompare(b)
}

export default function ColorReferencePage() {
  const [colors, setColors] = useState<ColorItem[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [familyFilter, setFamilyFilter] = useState<'all' | 'coated' | 'uncoated' | 'metallic'>('coated')
  const [copiedValue, setCopiedValue] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [sortBy, setSortBy] = useState<'code' | 'hue'>('code')
  const [selectedColor, setSelectedColor] = useState<ColorItem | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [compareMode, setCompareMode] = useState(false)
  const [compareList, setCompareList] = useState<ColorItem[]>([])
  const [pageTab, setPageTab] = useState<'browse' | 'match'>('browse')
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [pickedColor, setPickedColor] = useState<{ r: number, g: number, b: number, hex: string } | null>(null)
  const [matchFamily, setMatchFamily] = useState<'coated' | 'all'>('coated')
  const [matchResults, setMatchResults] = useState<(ColorItem & { matchPct: number })[]>([])
  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const imageRef = React.useRef<HTMLImageElement>(null)
  const magnifierCanvasRef = React.useRef<HTMLCanvasElement>(null)
  const [magnifierPos, setMagnifierPos] = useState<{ x: number, y: number } | null>(null)
  const [hoverHex, setHoverHex] = useState<string | null>(null)
  const [showComparePopup, setShowComparePopup] = useState(false)
  const [tintPct, setTintPct] = useState(100)
  const [pageInput, setPageInput] = useState('1')

  useEffect(() => {
    document.title = 'Color Reference Guide — Inkcredible Studios'
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex, nofollow'
    document.head.appendChild(meta)
    return () => { try { document.head.removeChild(meta) } catch {} }
  }, [])

  useEffect(() => {
    api.get('/color-reference').then(res => {
      setColors(res.data.colors || [])
    }).catch(() => {}).finally(() => setLoading(false))
    api.post('/color-reference/log-visit', { familyUsed: 'coated', searched: false, matchedFromImage: false }).catch(() => {})
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [familyFilter, search, sortBy])

  useEffect(() => {
    setPageInput(String(currentPage))
  }, [currentPage])

  const jumpToPage = (value: string) => {
    const num = parseInt(value, 10)
    if (isNaN(num)) { setPageInput(String(currentPage)); return }
    const clamped = Math.max(1, Math.min(totalPages, num))
    setCurrentPage(clamped)
  }

  const PAGE_SIZE = 120

  const filteredAll = useMemo(() => {
    let list = colors
    if (familyFilter !== 'all') list = list.filter(c => c.family === familyFilter)
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      list = list.filter(c =>
        c.code.toLowerCase().includes(q) ||
        getColorName(c.hex).includes(q)
      )
    }
    list = [...list]
    if (sortBy === 'code') {
      list.sort((a, b) => naturalCodeSort(a.code, b.code))
    } else {
      list.sort((a, b) => hexToHue(a.hex) - hexToHue(b.hex))
    }
    return list
  }, [colors, familyFilter, search, sortBy])

  const totalPages = Math.max(1, Math.ceil(filteredAll.length / PAGE_SIZE))
  const filtered = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE
    return filteredAll.slice(start, start + PAGE_SIZE)
  }, [filteredAll, currentPage])

  const handleCopy = (value: string, key: string) => {
    navigator.clipboard.writeText(value)
    setCopiedValue(key)
    setTimeout(() => setCopiedValue(null), 1200)
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      setUploadedImage(reader.result as string)
      setPickedColor(null)
      setMatchResults([])
    }
    reader.readAsDataURL(file)
  }

  const handleImageLoad = () => {
    const img = imageRef.current
    const canvas = canvasRef.current
    if (!img || !canvas) return
    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight
    const ctx = canvas.getContext('2d')
    if (ctx) ctx.drawImage(img, 0, 0)
  }

  const ZOOM_FACTOR = 8
  const SAMPLE_SIZE = 16
  const MAGNIFIER_SIZE = SAMPLE_SIZE * ZOOM_FACTOR

  const getPixelAt = (clientX: number, clientY: number) => {
    const img = imageRef.current
    const canvas = canvasRef.current
    if (!img || !canvas) return null
    const rect = img.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    const x = Math.floor((clientX - rect.left) * scaleX)
    const y = Math.floor((clientY - rect.top) * scaleY)
    return { x, y, rect, scaleX, scaleY, canvas }
  }

  const handleImageMouseMove = (e: React.MouseEvent<HTMLImageElement>) => {
    const point = getPixelAt(e.clientX, e.clientY)
    if (!point) return
    const { x, y, canvas } = point
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Draw zoomed preview
    const magCanvas = magnifierCanvasRef.current
    if (magCanvas) {
      const magCtx = magCanvas.getContext('2d')
      if (magCtx) {
        magCanvas.width = MAGNIFIER_SIZE
        magCanvas.height = MAGNIFIER_SIZE
        magCtx.imageSmoothingEnabled = false
        const half = Math.floor(SAMPLE_SIZE / 2)
        magCtx.clearRect(0, 0, MAGNIFIER_SIZE, MAGNIFIER_SIZE)
        magCtx.drawImage(
          canvas,
          x - half, y - half, SAMPLE_SIZE, SAMPLE_SIZE,
          0, 0, MAGNIFIER_SIZE, MAGNIFIER_SIZE
        )
        // Crosshair
        magCtx.strokeStyle = 'rgba(255,255,255,0.9)'
        magCtx.lineWidth = 1
        magCtx.beginPath()
        magCtx.moveTo(MAGNIFIER_SIZE / 2, 0)
        magCtx.lineTo(MAGNIFIER_SIZE / 2, MAGNIFIER_SIZE)
        magCtx.moveTo(0, MAGNIFIER_SIZE / 2)
        magCtx.lineTo(MAGNIFIER_SIZE, MAGNIFIER_SIZE / 2)
        magCtx.stroke()
        // Center pixel highlight box
        magCtx.strokeStyle = '#f97316'
        magCtx.lineWidth = 2
        magCtx.strokeRect(MAGNIFIER_SIZE / 2 - ZOOM_FACTOR / 2, MAGNIFIER_SIZE / 2 - ZOOM_FACTOR / 2, ZOOM_FACTOR, ZOOM_FACTOR)
      }
    }

    // Live hex readout at center pixel
    try {
      const pixel = ctx.getImageData(x, y, 1, 1).data
      const toHex = (n: number) => n.toString(16).padStart(2, '0')
      setHoverHex(`#${toHex(pixel[0])}${toHex(pixel[1])}${toHex(pixel[2])}`.toUpperCase())
    } catch (_) {}

    setMagnifierPos({ x: e.clientX, y: e.clientY })
  }

  const handleImageMouseLeave = () => {
    setMagnifierPos(null)
    setHoverHex(null)
  }

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    const point = getPixelAt(e.clientX, e.clientY)
    if (!point) return
    const { x, y, canvas } = point
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const pixel = ctx.getImageData(x, y, 1, 1).data
    const r = pixel[0], g = pixel[1], b = pixel[2]
    const toHex = (n: number) => n.toString(16).padStart(2, '0')
    const hex = `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
    setPickedColor({ r, g, b, hex })

    const pool = matchFamily === 'all' ? colors : colors.filter(c => c.family === 'coated')
    const maxDist = Math.sqrt(255 * 255 * 3)
    const ranked = pool.map(c => {
      const cr = hexToRgb(c.hex)
      const dist = rgbDistance(r, g, b, cr.r, cr.g, cr.b)
      const matchPct = Math.round((1 - dist / maxDist) * 100)
      return { ...c, matchPct }
    }).sort((x, y) => y.matchPct - x.matchPct).slice(0, 5)
    setMatchResults(ranked)
  }

  const toggleCompareMode = () => {
    setCompareMode(m => !m)
    if (compareMode) setCompareList([])
  }

  const handleSwatchClick = (c: ColorItem) => {
    if (!compareMode) { setSelectedColor(c); setTintPct(100); return }
    setCompareList(list => {
      const exists = list.some(x => x.code === c.code && x.family === c.family)
      if (exists) return list.filter(x => !(x.code === c.code && x.family === c.family))
      if (list.length >= 4) return list
      return [...list, c]
    })
  }

  const isInCompare = (c: ColorItem) => compareList.some(x => x.code === c.code && x.family === c.family)

  useEffect(() => {
    if (!pickedColor) return
    const pool = matchFamily === 'all' ? colors : colors.filter(c => c.family === 'coated')
    const maxDist = Math.sqrt(255 * 255 * 3)
    const ranked = pool.map(c => {
      const cr = hexToRgb(c.hex)
      const dist = rgbDistance(pickedColor.r, pickedColor.g, pickedColor.b, cr.r, cr.g, cr.b)
      const matchPct = Math.round((1 - dist / maxDist) * 100)
      return { ...c, matchPct }
    }).sort((x, y) => y.matchPct - x.matchPct).slice(0, 5)
    setMatchResults(ranked)
  }, [matchFamily])

  const displayHex = selectedColor ? applyTint(selectedColor.hex, tintPct) : null
  const rgb = displayHex ? hexToRgb(displayHex) : null
  const cmyk = rgb ? rgbToCmyk(rgb.r, rgb.g, rgb.b) : null

  return (
    <div className="min-h-screen bg-[#fdfcfb] dark:bg-[#0a1929]">
      <div className="border-b border-gray-100 dark:border-[#1e3450] bg-white dark:bg-[#0f2137] px-4 sm:px-8 py-3.5">
        <img src="/Full_Color_Logo.svg" alt="Inkcredible Studios" className="h-7 object-contain dark:hidden" />
        <img src="/White_Color_Logo.svg" alt="Inkcredible Studios" className="h-7 object-contain hidden dark:block" />
      </div>
      <div className="max-w-6xl mx-auto p-4 sm:p-8">
        <div className="mb-6">
          <h1 className="font-display text-3xl font-bold text-navy-900 dark:text-white">Color Reference Guide</h1>
          <p className="text-gray-400 mt-1">Search standard spot colors by code to find the matching hex value</p>
        </div>

        {/* Page tabs */}
        <div className="flex gap-1 bg-gray-100 dark:bg-[#112240] rounded-2xl p-1 mb-6 max-w-md">
          <button onClick={() => setPageTab('browse')}
            className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${pageTab === 'browse' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500 hover:text-navy-700 dark:hover:text-gray-300'}`}>
            Browse Colors
          </button>
          <button onClick={() => setPageTab('match')}
            className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${pageTab === 'match' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500 hover:text-navy-700 dark:hover:text-gray-300'}`}>
            Match from Image
          </button>
        </div>

        {pageTab === 'browse' && (
        <>
        {/* Search + filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by code (e.g. 186, 7406)"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-gray-200 dark:border-[#1e3450] bg-white dark:bg-[#0f2137] text-navy-800 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 transition-colors" title="Clear search">
                <X size={15} />
              </button>
            )}
          </div>
          <p className="text-[10px] text-gray-400 mt-1.5 px-1">Search by color code (e.g. 286, 7406) or by color name (e.g. blue, red, green, orange)</p>
          <div className="flex gap-1 bg-gray-100 dark:bg-[#112240] rounded-xl p-1">
            {(['all', 'coated', 'uncoated', 'metallic'] as const).map(f => (
              <button key={f} onClick={() => setFamilyFilter(f)}
                className={`px-3 py-2 rounded-lg text-xs font-medium capitalize transition-colors ${familyFilter === f ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500 hover:text-navy-700 dark:hover:text-gray-300'}`}>
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Sort + view toggle */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ArrowUpDown size={13} className="text-gray-400" />
            <select value={sortBy} onChange={e => setSortBy(e.target.value as any)}
              className="text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 rounded-lg px-2.5 py-1.5 border-none focus:outline-none focus:ring-2 focus:ring-orange-200">
              <option value="code">Sort: Code</option>
              <option value="hue">Sort: Color order</option>
            </select>
          </div>
          <div className="flex gap-1 bg-gray-100 dark:bg-[#112240] rounded-xl p-1">
            <button onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-400 hover:text-navy-700 dark:hover:text-gray-300'}`} title="Grid view">
              <Grid size={14} />
            </button>
            <button onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-400 hover:text-navy-700 dark:hover:text-gray-300'}`} title="List view">
              <List size={14} />
            </button>
          </div>
          <button onClick={toggleCompareMode}
            className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors ${compareMode ? 'bg-orange-500 text-white' : 'bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#1c3a5e]'}`}>
            {compareMode ? 'Exit Compare' : 'Compare Colors'}
          </button>
        </div>

        {loading ? (
          <div className="py-20 text-center text-gray-400">Loading colors...</div>
        ) : (
          <>
            <p className="text-xs text-gray-400 mb-3">Showing {filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredAll.length)} of {filteredAll.length} colors</p>

            {viewMode === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {filtered.map(c => (
                  <button key={`${c.family}-${c.code}`} onClick={() => handleSwatchClick(c)}
                    className={`text-left rounded-xl border overflow-hidden bg-white dark:bg-[#0f2137] hover:shadow-md transition-shadow relative ${compareMode && isInCompare(c) ? 'border-orange-400 ring-2 ring-orange-200' : 'border-gray-100 dark:border-[#1e3450]'}`}>
                    {compareMode && isInCompare(c) && (
                      <div className="absolute top-1.5 right-1.5 bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold z-10">✓</div>
                    )}
                    <div className="h-16 w-full" style={{ backgroundColor: c.hex }} />
                    <div className="p-2.5">
                      <p className="text-xs font-mono font-semibold text-navy-800 dark:text-white truncate">{c.code}</p>
                      <span className="text-[10px] text-gray-400 font-mono">{c.hex}</span>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-gray-100 dark:border-[#1e3450] overflow-hidden bg-white dark:bg-[#0f2137]">
                {filtered.map((c, i) => (
                  <button key={`${c.family}-${c.code}`} onClick={() => handleSwatchClick(c)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50/30 dark:hover:bg-orange-900/5 transition-colors text-left ${i !== filtered.length - 1 ? 'border-b border-gray-50 dark:border-[#1e3450]' : ''} ${compareMode && isInCompare(c) ? 'bg-orange-50/50 dark:bg-orange-900/10' : ''}`}>
                    <div className="w-8 h-8 rounded-lg flex-shrink-0 relative" style={{ backgroundColor: c.hex }}>
                      {compareMode && isInCompare(c) && (
                        <div className="absolute -top-1 -right-1 bg-orange-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[8px] font-bold">✓</div>
                      )}
                    </div>
                    <span className="text-sm font-mono font-semibold text-navy-800 dark:text-white flex-shrink-0 w-20">{c.code}</span>
                    <span className="text-xs text-gray-400 font-mono flex-shrink-0 w-20">{c.hex}</span>
                    <span className="text-[10px] text-gray-300 capitalize">{c.family}</span>
                  </button>
                ))}
              </div>
            )}

            {filtered.length === 0 && (
              <div className="py-16 text-center text-gray-300 dark:text-gray-600 text-sm">No colors found matching "{search}"</div>
            )}

            {filteredAll.length > PAGE_SIZE && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-200 dark:hover:bg-[#1c3a5e] transition-colors">
                  Previous
                </button>
                <span className="text-xs text-gray-400 px-1">Page</span>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={pageInput}
                  onChange={e => setPageInput(e.target.value)}
                  onBlur={() => jumpToPage(pageInput)}
                  onKeyDown={e => { if (e.key === 'Enter') jumpToPage(pageInput) }}
                  className="w-14 text-center text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-white rounded-lg py-1.5 border-none focus:outline-none focus:ring-2 focus:ring-orange-200"
                />
                <span className="text-xs text-gray-400 px-1">of {totalPages}</span>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-200 dark:hover:bg-[#1c3a5e] transition-colors">
                  Next
                </button>
              </div>
            )}
          </>
        )}

        <p className="text-xs text-gray-400 mt-8 text-center">
          Color values shown are approximate digital representations intended for internal reference only and may not exactly match physical print or plating output.
        </p>
        </>
        )}

        {pageTab === 'match' && (
          <div className="space-y-5">
            {!uploadedImage ? (
              <label className="block border-2 border-dashed border-gray-200 dark:border-[#1e3450] rounded-2xl py-16 text-center cursor-pointer hover:border-orange-300 transition-colors">
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                <p className="text-sm text-gray-400">Click to upload an image, or drag and drop</p>
                <p className="text-xs text-gray-300 mt-1">PNG, JPG — used only in your browser, never uploaded to our server</p>
              </label>
            ) : (
              <div className="flex flex-col sm:flex-row gap-5">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-400">Click anywhere on the image to pick a color</p>
                    <button onClick={() => { setUploadedImage(null); setPickedColor(null); setMatchResults([]) }} className="text-xs text-gray-400 hover:text-red-500 transition-colors">Remove image</button>
                  </div>
                  <div className="rounded-xl overflow-hidden border border-gray-100 dark:border-[#1e3450] bg-gray-50 dark:bg-[#112240]">
                    <img ref={imageRef} src={uploadedImage} onLoad={handleImageLoad} onClick={handleImageClick}
                      onMouseMove={handleImageMouseMove} onMouseLeave={handleImageMouseLeave}
                      className="w-full max-h-[500px] object-contain cursor-crosshair" alt="Uploaded reference" />
                  </div>
                  <canvas ref={canvasRef} className="hidden" />

                  {magnifierPos && (
                    <div
                      className="fixed z-50 pointer-events-none rounded-full overflow-hidden shadow-2xl border-2 border-white dark:border-[#1c3a5e]"
                      style={{ left: magnifierPos.x + 24, top: magnifierPos.y - MAGNIFIER_SIZE - 24, width: MAGNIFIER_SIZE, height: MAGNIFIER_SIZE }}
                    >
                      <canvas ref={magnifierCanvasRef} className="w-full h-full" />
                      {hoverHex && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] font-mono text-center py-1">
                          {hoverHex}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="w-full sm:w-72 flex-shrink-0 space-y-4">
                  {pickedColor && (
                    <div className="rounded-xl border border-gray-100 dark:border-[#1e3450] overflow-hidden bg-white dark:bg-[#0f2137]">
                      <div className="h-16 w-full" style={{ backgroundColor: pickedColor.hex }} />
                      <div className="p-3">
                        <p className="text-xs text-gray-400">Picked color</p>
                        <p className="text-sm font-mono font-semibold text-navy-800 dark:text-white">{pickedColor.hex}</p>
                        <p className="text-[10px] text-gray-400 font-mono">RGB {pickedColor.r}, {pickedColor.g}, {pickedColor.b}</p>
                      </div>
                    </div>
                  )}

                  {pickedColor && (
                    <>
                      <div className="flex gap-1 bg-gray-100 dark:bg-[#112240] rounded-xl p-1">
                        <button onClick={() => setMatchFamily('coated')}
                          className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${matchFamily === 'coated' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500'}`}>
                          Coated
                        </button>
                        <button onClick={() => setMatchFamily('all')}
                          className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${matchFamily === 'all' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500'}`}>
                          All families
                        </button>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-navy-700 dark:text-gray-300 mb-2">Top 5 matches</p>
                        <div className="space-y-2">
                          {matchResults.map(r => (
                            <button key={`${r.family}-${r.code}`} onClick={() => { setSelectedColor(r); setTintPct(100) }}
                              className="w-full flex items-center gap-2.5 bg-gray-50 dark:bg-[#112240] hover:bg-gray-100 dark:hover:bg-[#1c3a5e] rounded-xl px-3 py-2 transition-colors text-left">
                              <div className="w-8 h-8 rounded-lg flex-shrink-0" style={{ backgroundColor: r.hex }} />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-mono font-semibold text-navy-800 dark:text-white truncate">{r.code}</p>
                                <p className="text-[10px] text-gray-400 capitalize">{r.family}</p>
                              </div>
                              <span className="text-xs font-bold text-orange-500 flex-shrink-0">{r.matchPct}%</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Compare tray */}
      {compareMode && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-[#0f2137] border-t border-gray-200 dark:border-[#1e3450] shadow-2xl z-40 px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-1 overflow-x-auto">
              <p className="text-xs font-semibold text-navy-700 dark:text-gray-300 flex-shrink-0">Compare ({compareList.length}/4):</p>
              {compareList.length === 0 ? (
                <p className="text-xs text-gray-300 dark:text-gray-600">Click on colors to add them here</p>
              ) : (
                <div className="flex gap-1.5">
                  {compareList.map(c => (
                    <div key={`${c.family}-${c.code}`} className="flex items-center gap-1 bg-gray-50 dark:bg-[#112240] rounded-lg pl-1 pr-1.5 py-1 flex-shrink-0">
                      <div className="w-4 h-4 rounded flex-shrink-0" style={{ backgroundColor: c.hex }} />
                      <span className="text-[10px] font-mono text-navy-700 dark:text-gray-300">{c.code}</span>
                      <button onClick={() => setCompareList(list => list.filter(x => !(x.code === c.code && x.family === c.family)))}
                        className="text-gray-300 hover:text-red-500 transition-colors">
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {compareList.length > 0 && (
                <button onClick={() => setCompareList([])} className="text-xs text-gray-400 hover:text-red-500 transition-colors">Clear</button>
              )}
              <button onClick={() => setShowComparePopup(true)} disabled={compareList.length < 2}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-orange-500 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-orange-600 transition-colors">
                Show Comparison
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comparison popup */}
      {showComparePopup && compareList.length >= 2 && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowComparePopup(false)}>
          <div className="bg-white dark:bg-[#0f2137] rounded-2xl max-w-3xl w-full overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-[#1e3450]">
              <p className="font-display font-semibold text-navy-900 dark:text-white">Color Comparison</p>
              <div className="flex items-center gap-3">
                <button onClick={() => handleCopy(compareList.map(c => `${c.code}: ${c.hex}`).join('\n'), 'compare-all')}
                  className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-orange-500 transition-colors">
                  {copiedValue === 'compare-all' ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
                  {copiedValue === 'compare-all' ? 'Copied all' : 'Copy all'}
                </button>
                <button onClick={() => setShowComparePopup(false)} className="text-gray-300 hover:text-gray-500 transition-colors">
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="flex">
              {compareList.map(c => {
                const r = hexToRgb(c.hex)
                const k = rgbToCmyk(r.r, r.g, r.b)
                const compareKey = `compare-${c.family}-${c.code}`
                return (
                  <div key={`${c.family}-${c.code}`} className="flex-1">
                    <div className="h-32 w-full" style={{ backgroundColor: c.hex }} />
                    <div className="p-3 text-center border-r border-gray-100 dark:border-[#1e3450] last:border-r-0">
                      <p className="font-display font-bold text-navy-900 dark:text-white">{c.code}</p>
                      <p className="text-[10px] text-gray-400 capitalize mb-2">{c.family}</p>
                      <p className="text-xs font-mono text-gray-500 dark:text-gray-400">{c.hex}</p>
                      <p className="text-xs font-mono text-gray-500 dark:text-gray-400">RGB {r.r}, {r.g}, {r.b}</p>
                      <p className="text-xs font-mono text-gray-500 dark:text-gray-400">CMYK {k.c}, {k.m}, {k.y}, {k.k}</p>
                      <button onClick={() => handleCopy(c.hex, compareKey)}
                        className="mt-2 inline-flex items-center gap-1 text-[10px] text-gray-400 hover:text-orange-500 transition-colors border border-gray-200 dark:border-[#1e3450] rounded-lg px-2 py-1">
                        {copiedValue === compareKey ? <Check size={11} className="text-green-500" /> : <Copy size={11} />}
                        {copiedValue === compareKey ? 'Copied' : 'Copy hex'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Detail popup */}
      {selectedColor && rgb && cmyk && displayHex && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setSelectedColor(null)}>
          <div className="bg-white dark:bg-[#0f2137] rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="h-56 w-full relative" style={{ backgroundColor: displayHex }}>
              <button onClick={() => setSelectedColor(null)} className="absolute top-3 right-3 bg-white/80 hover:bg-white rounded-full p-1.5 transition-colors">
                <X size={14} className="text-navy-800" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-display font-bold text-xl text-navy-900 dark:text-white">{selectedColor.code}</p>
                <span className="text-[10px] capitalize bg-gray-100 dark:bg-[#112240] text-gray-400 px-2 py-1 rounded-full">{selectedColor.family}</span>
              </div>

              {/* Tint control */}
              <div className="bg-gray-50 dark:bg-[#112240] rounded-lg px-3 py-2.5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-400">Tint</span>
                  <span className="text-xs font-mono font-semibold text-navy-700 dark:text-white">{tintPct}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setTintPct(p => Math.max(0, p - 5))}
                    className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-lg bg-white dark:bg-[#1c3a5e] text-navy-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#234268] transition-colors text-sm font-bold">−</button>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={tintPct}
                    onChange={e => setTintPct(parseInt(e.target.value, 10))}
                    className="flex-1 accent-orange-500"
                  />
                  <button onClick={() => setTintPct(p => Math.min(100, p + 5))}
                    className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-lg bg-white dark:bg-[#1c3a5e] text-navy-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#234268] transition-colors text-sm font-bold">+</button>
                </div>
                {tintPct !== 100 && (
                  <button onClick={() => setTintPct(100)} className="text-[10px] text-orange-500 hover:text-orange-600 mt-1.5 transition-colors">Reset to original</button>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between bg-gray-50 dark:bg-[#112240] rounded-lg px-3 py-2">
                  <span className="text-xs text-gray-400">HEX {tintPct !== 100 ? `(${tintPct}% tint)` : ''}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-medium text-navy-800 dark:text-white">{displayHex}</span>
                    <button onClick={() => handleCopy(displayHex, 'hex')} className="text-gray-300 hover:text-orange-500 transition-colors">
                      {copiedValue === 'hex' ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between bg-gray-50 dark:bg-[#112240] rounded-lg px-3 py-2">
                  <span className="text-xs text-gray-400">RGB</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-medium text-navy-800 dark:text-white">{rgb.r}, {rgb.g}, {rgb.b}</span>
                    <button onClick={() => handleCopy(`${rgb.r}, ${rgb.g}, ${rgb.b}`, 'rgb')} className="text-gray-300 hover:text-orange-500 transition-colors">
                      {copiedValue === 'rgb' ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between bg-gray-50 dark:bg-[#112240] rounded-lg px-3 py-2">
                  <span className="text-xs text-gray-400">CMYK (approx)</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-medium text-navy-800 dark:text-white">{cmyk.c}, {cmyk.m}, {cmyk.y}, {cmyk.k}</span>
                    <button onClick={() => handleCopy(`${cmyk.c}, ${cmyk.m}, ${cmyk.y}, ${cmyk.k}`, 'cmyk')} className="text-gray-300 hover:text-orange-500 transition-colors">
                      {copiedValue === 'cmyk' ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
